import {
  type FeatureFlagKey,
  isFlagKeyRegistered,
} from '../registry/feature-flags';
import type { UnifiedFlagResult } from '../types/shared';

import { parseVariantAttachment } from './parse-variant-attachment';
import type { FlagEvaluationResult } from './types';
import { isBooleanEvaluationResult, isVariantEvaluationResult } from './types';

/**
 * Processed single flag result
 * For boolean flags: has `enabled` property
 * For variant flags: has `match`, `variantKey`, and `variantAttachment` (parsed as object) properties
 */
export type ProcessedFlagResult = {
  enabled?: boolean;
  variantKey?: string;
  variantAttachment?: Record<string, unknown> | null;
  match?: boolean;
  result: FlagEvaluationResult;
};

/**
 * Processed multiple flags result
 */
export type ProcessedFlagsResult = {
  flags: Partial<Record<FeatureFlagKey, boolean>>;
  results: Partial<Record<FeatureFlagKey, UnifiedFlagResult>>;
};

/**
 * Process a single flag evaluation result into a standardized format
 * For boolean flags: returns enabled property
 * For variant flags: returns match, variantKey, and variantAttachment properties
 *
 * @param result Flag evaluation result (boolean or variant)
 * @param defaultValue Default value to use if result is invalid
 * @returns Processed result with enabled state (boolean) or match state (variant)
 */
export function processSingleFlagResult(
  result: FlagEvaluationResult,
  defaultValue = false,
): ProcessedFlagResult {
  // Boolean flag result
  if (isBooleanEvaluationResult(result)) {
    return {
      enabled: result.enabled,
      result,
    };
  }

  // Variant flag result
  if (isVariantEvaluationResult(result)) {
    return {
      match: result.match,
      variantKey: result.variantKey,
      variantAttachment: parseVariantAttachment(result.variantAttachment),
      result,
    };
  }

  // Fallback (should not happen with proper registry)
  return {
    enabled: defaultValue,
    result,
  };
}

/**
 * Process multiple flag evaluation results into a standardized format
 * Converts results map into a simple boolean flags map and unified results
 *
 * @param results Map of flag keys to evaluation results
 * @param defaultValue Default value to use for invalid results
 * @returns Object with flags map (boolean) and unified results
 */
export function processMultipleFlagResults(
  results: Record<string, FlagEvaluationResult>,
  defaultValue = false,
): ProcessedFlagsResult {
  const flags: Partial<Record<FeatureFlagKey, boolean>> = {};
  const unifiedResults: Partial<Record<FeatureFlagKey, UnifiedFlagResult>> = {};

  for (const [key, result] of Object.entries(results)) {
    // Security: Validate key is a registered flag to prevent prototype pollution
    // Skip any unregistered, malicious, or unexpected keys (e.g., __proto__)
    if (!isFlagKeyRegistered(key)) {
      continue;
    }

    // Safe to use as FeatureFlagKey after validation
    const flagKey = key;

    if (isBooleanEvaluationResult(result)) {
      flags[flagKey] = result.enabled;
      unifiedResults[flagKey] = {
        type: 'boolean',
        flagKey: result.flagKey,
        reason: result.reason,
        context: result.context,
        timestamp: result.timestamp,
      };
    } else if (isVariantEvaluationResult(result)) {
      flags[flagKey] = result.match;
      unifiedResults[flagKey] = {
        type: 'variant',
        flagKey: result.flagKey,
        reason: result.reason,
        context: result.context,
        timestamp: result.timestamp,
        match: result.match,
        variantKey: result.variantKey,
        variantAttachment: parseVariantAttachment(result.variantAttachment),
      };
    } else {
      flags[flagKey] = defaultValue;
      unifiedResults[flagKey] = {
        type: 'boolean',
        flagKey,
        reason: 'unknown',
      };
    }
  }

  return {
    flags,
    results: unifiedResults,
  };
}

/**
 * Extract variant information from a unified flag result.
 * Uses the `type` discriminant to narrow the result and return variant-specific properties.
 *
 * @param result Unified flag result
 * @returns Variant information or empty object if not a variant flag
 */
export function extractVariantInfo(result: UnifiedFlagResult | null): {
  variantKey?: string;
  variantAttachment?: Record<string, unknown> | null;
  match?: boolean;
} {
  if (!result || result.type !== 'variant') {
    return {};
  }

  return {
    variantKey: result.variantKey,
    variantAttachment: result.variantAttachment,
    match: result.match,
  };
}

/**
 * Get enabled state from a unified flag result.
 * Uses the `type` discriminant to determine the correct property.
 * For boolean flags, the caller should use the flags map instead.
 * For variant flags, uses the `match` property.
 *
 * @param result Unified flag result
 * @param defaultValue Default value if result is invalid
 * @returns Boolean enabled state
 */
export function getEnabledState(
  result: UnifiedFlagResult | null,
  defaultValue = false,
): boolean {
  if (!result) {
    return defaultValue;
  }

  if (result.type === 'variant') {
    return result.match;
  }

  // For boolean flags, we can't determine from the result alone
  // The caller should use the flags map instead
  return defaultValue;
}
